<script setup lang="ts">
import { onMounted, ref } from "vue";
import { cancelRedemption, getRedemption, listRedemptions, shipRedemption, type Redemption } from "../api/points-admin";

const items = ref<Redemption[]>([]);
const selected = ref<Redemption | null>(null);
const status = ref("");
const company = ref("");
const tracking = ref("");
const reason = ref("管理员取消");
const message = ref("");
const actionBusy = ref<"ship" | "cancel" | null>(null);

function messageFrom(errorValue: unknown, fallback: string): string {
  return errorValue instanceof Error && errorValue.message ? errorValue.message : fallback;
}

async function load(): Promise<boolean> {
  try {
    items.value = await listRedemptions(status.value);
    return true;
  } catch (errorValue) {
    message.value = messageFrom(errorValue, "积分兑换订单加载失败");
    return false;
  }
}

async function open(no: string): Promise<void> {
  try {
    selected.value = await getRedemption(no);
  } catch (errorValue) {
    message.value = messageFrom(errorValue, "积分兑换订单详情加载失败");
  }
}

async function ship(): Promise<void> {
  if (!selected.value || actionBusy.value) return;
  const redemptionNo = selected.value.redemptionNo;
  actionBusy.value = "ship";
  message.value = "";
  try {
    selected.value = await shipRedemption(redemptionNo, company.value, tracking.value);
    const refreshed = await load();
    message.value = refreshed
      ? `兑换单 ${redemptionNo} 已发货`
      : `兑换单 ${redemptionNo} 已发货，但列表刷新失败，请重新加载。`;
  } catch (errorValue) {
    message.value = messageFrom(errorValue, "发货失败，请稍后重试");
  } finally {
    actionBusy.value = null;
  }
}

async function cancel(): Promise<void> {
  if (!selected.value || actionBusy.value) return;
  const redemptionNo = selected.value.redemptionNo;
  if (!window.confirm(`确认取消兑换单 ${redemptionNo} 并退还积分？\n该操作将实际变更订单状态并退还积分。`)) return;
  actionBusy.value = "cancel";
  message.value = "";
  try {
    selected.value = await cancelRedemption(redemptionNo, reason.value);
    const refreshed = await load();
    message.value = refreshed
      ? `兑换单 ${redemptionNo} 已取消并退还积分`
      : `兑换单 ${redemptionNo} 已取消并退还积分，但列表刷新失败，请重新加载。`;
  } catch (errorValue) {
    message.value = messageFrom(errorValue, "取消兑换单失败，请稍后重试");
  } finally {
    actionBusy.value = null;
  }
}

onMounted(() => {
  void load();
});
</script><template>
  <section>
    <header class="page-header">
      <div>
        <h1>积分兑换订单</h1><p class="muted">
          处理发货及未发货取消。
        </p>
      </div><select
        v-model="status"
        @change="load"
      >
        <option value="">
          全部
        </option><option
          v-for="s in ['CREATED','PROCESSING','SHIPPED','COMPLETED','CANCELED']"
          :key="s"
        >
          {{ s }}
        </option>
      </select>
    </header><p v-if="message">
      {{ message }}
    </p><table>
      <thead><tr><th>兑换单号</th><th>用户商品</th><th>积分</th><th>收货人</th><th>状态</th><th>创建时间</th></tr></thead><tbody>
        <tr
          v-for="o in items"
          :key="o.id"
          @click="open(o.redemptionNo)"
        >
          <td>{{ o.redemptionNo }}</td><td>{{ o.items[0]?.name }}</td><td>{{ o.pointsAmount }}</td><td>{{ o.receiver.name }}</td><td>{{ o.status }}</td><td>{{ o.createdAt }}</td>
        </tr>
      </tbody>
    </table><article
      v-if="selected"
      class="panel detail"
    >
      <h2>{{ selected.redemptionNo }}</h2><div
        v-for="item in selected.items"
        :key="item.id"
      >
        <p>{{ item.productNo }} · {{ item.name }} · {{ item.pointsCost }}积分 × {{ item.quantity }}</p><img
          v-if="item.imageUrl"
          :src="item.imageUrl"
          alt=""
          width="120"
        >
      </div><p>收货：{{ selected.receiver.name }} {{ selected.receiver.phone }} {{ selected.receiver.province }}{{ selected.receiver.city }}{{ selected.receiver.district }}{{ selected.receiver.detail }}</p><p>物流：{{ selected.shippingCompany||'-' }} / {{ selected.trackingNo||'-' }}；取消原因：{{ selected.cancelReason||'-' }}</p><form
        v-if="['CREATED','PROCESSING'].includes(selected.status)"
        class="inline-form"
        @submit.prevent="ship"
      >
        <label>物流公司<input
          v-model="company"
          required
        ></label><label>物流单号<input
          v-model="tracking"
          required
        ></label><button :disabled="actionBusy !== null">
          {{ actionBusy === "ship" ? "发货中…" : "发货" }}
        </button>
      </form><form
        v-if="['CREATED','PROCESSING'].includes(selected.status)"
        class="inline-form"
        @submit.prevent="cancel"
      >
        <label>取消原因<input
          v-model="reason"
          required
        ></label><button :disabled="actionBusy !== null">
          {{ actionBusy === "cancel" ? "取消中…" : "取消并退还积分" }}
        </button>
      </form>
    </article>
  </section>
</template><style scoped>table{width:100%;border-collapse:collapse;background:#fff}th,td{padding:12px;border-bottom:1px solid #e2e7ef;text-align:left}.detail{margin-top:20px}</style>
